import React from "react";
import { AutoReorder } from "~/ProductDetails/ProductDetailPageResult";
import isGroupMemberSelected from "../isGroupMemberSelected";
import { AccessorySelections } from "./Accessories";
import getMinBuyForQuantityDropdown from "./getMinBuyForQuantityDropdown";
import ValidationError from "./ValidationError";

export const createVgSelectionError = () => {
  const validationMessage = "Please select required product options.";

  return new ValidationError(
    validationMessage,
    groupingSelected,
    "vgSelection"
  );
};

export const minimumQuantity = ({
  product,
  quantity,
}: {
  product: ValidationProduct;
  quantity: number;
}) => {
  const updatedStartBuy = getMinBuyForQuantityDropdown({
    minBuy: product.startBuy,
    mustBuy: product.mustBuy,
  });

  if (quantity < updatedStartBuy) {
    throw new ValidationError(
      `Please enter a quantity of ${updatedStartBuy} or more.`,
      minimumQuantity
    );
  }
};

export const mustBuy = ({
  product,
  quantity,
}: {
  product: ValidationProduct;
  quantity: number;
}) => {
  if (quantity % product.mustBuy) {
    throw new ValidationError(
      `Please enter a quantity as a multiple of ${product.mustBuy}.`,
      mustBuy
    );
  }
};

export const groupingSelected = ({
  product,
}: {
  product: ValidationProduct;
}) => {
  const hasSelectedGroupMember = isGroupMemberSelected(product);

  if (!hasSelectedGroupMember) {
    const validationError = createVgSelectionError();

    throw validationError;
  }
};

export const autoReorder = ({
  autoReorderSelections = {
    isToggleEnabled: false,
    isAddToExistingAutoReorder: false,
    newAutoReorderInterval: "0",
    selectedExistingAutoReorder: {
      isActive: false,
      name: "",
      creditCardId: 0,
      desiredOrderDate: "",
      id: 0,
    },
  },
}: {
  autoReorderSelections?: AutoReorder;
}) => {
  if (autoReorderSelections?.isToggleEnabled) {
    if (autoReorderSelections.isAddToExistingAutoReorder) {
      if (autoReorderSelections.selectedExistingAutoReorder.id === 0) {
        throw new ValidationError(
          "Please select an existing Auto Reorder",
          autoReorder
        );
      }
    } else if (autoReorderSelections.newAutoReorderInterval === "0") {
      throw new ValidationError(
        "Please Select Auto Reorder interval",
        autoReorder
      );
    }
  }
};

export const accessories = ({
  product,
  accessorySelections = {},
  setInvalidAccessories,
}: {
  product: ValidationProduct;
  accessorySelections?: AccessorySelections;
  setInvalidAccessories?: React.Dispatch<
    React.SetStateAction<readonly string[]>
  >;
}) => {
  if (!product.accessories) {
    return;
  }

  const invalidAccessories: string[] = [];
  product.accessories.forEach((accessory) => {
    if (!accessory.required || accessorySelections[accessory.name]) return;

    invalidAccessories.push(accessory.name);
  });

  setInvalidAccessories?.(invalidAccessories);

  if (invalidAccessories.length > 0) {
    throw new ValidationError(
      "Please select required accessories and options.",
      accessories
    );
  }
};

export type Validations =
  | typeof groupingSelected
  | typeof mustBuy
  | typeof minimumQuantity
  | typeof autoReorder
  | typeof accessories;

export interface ValidationProduct {
  grouping: boolean;
  groupingAttributes: {
    isVirtual: boolean;
    groupMembers: {
      configurationOptions: { name: string; value: string }[];
      itemNumber: string;
    }[];
  };
  itemNumber: string;
  mustBuy: number;
  startBuy: number;
  accessories?: {
    name: string;
    required: boolean;
    options: {
      id: number;
      description: string;
    }[];
  }[];
}
interface Params {
  product: ValidationProduct;
  quantity: number;
  autoReorderSelections?: AutoReorder;
  accessorySelections?: AccessorySelections;
  setInvalidAccessories?: React.Dispatch<
    React.SetStateAction<readonly string[]>
  >;
  validationsToRun?: (({
    product,
    quantity,
    autoReorderSelections,
    accessorySelections,
    setInvalidAccessories,
  }: {
    product: ValidationProduct;
    quantity: number;
    autoReorderSelections?: AutoReorder;
    accessorySelections?: AccessorySelections;
    setInvalidAccessories?: React.Dispatch<
      React.SetStateAction<readonly string[]>
    >;
  }) => void)[];
}

export const getValidationError = ({
  product,
  quantity,
  autoReorderSelections,
  accessorySelections,
  setInvalidAccessories,
  validationsToRun = [
    groupingSelected,
    minimumQuantity,
    mustBuy,
    accessories,
    autoReorder,
  ],
}: Params): ValidationError | undefined => {
  try {
    for (const validation of validationsToRun) {
      validation({
        product,
        quantity,
        autoReorderSelections,
        setInvalidAccessories,
        accessorySelections,
      });
    }

    return undefined;
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return error as ValidationError;
  }
};
